import { api } from '@/lib/api';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useServerNotifications, notificationTarget } from '@/hooks/queries/useServerNotifications';
import { usePublicOrders } from '@/hooks/queries/usePublicOrders';
import { useOrderAccess } from '@/hooks/queries/useOrderAccess';
import { useAuth } from '@/contexts/AuthContext';
import { useShop } from '@/contexts/ShopContext';

/** One polling owner for the entire shell. Headers share its query cache. */
export function OrderNotificationMonitor() {
  const enabled = useOrderAccess();
  const query = useServerNotifications(enabled ? 15_000 : 300_000);
  usePublicOrders({ workflow: 'new', per_page: 1 }, { enabled, poll: true });
  const { user } = useAuth();
  const { currentShop } = useShop();
  const scope = `${user?.id}:${currentShop?.id}`;
  const seen = useRef<{ scope: string; ids: Set<number> } | null>(null);
  const navigate = useNavigate();
  const client = useQueryClient();
  useEffect(() => {
    return () => {
      toast.dismiss('new-public-orders');
    };
  }, [scope]);
  useEffect(() => {
    const notifications = query.data?.data;
    if (!notifications) return;
    if (seen.current?.scope !== scope) {
      seen.current = { scope, ids: new Set(notifications.map((item) => item.id)) };
      return;
    }
    const knownIds = seen.current.ids;
    const fresh = notifications.filter(
      (item) => !knownIds.has(item.id) && !item.is_read && item.type === 'public_order.created',
    );
    notifications.forEach((item) => knownIds.add(item.id));
    if (!enabled || !fresh.length) return;
    void client.invalidateQueries({ queryKey: ['public-orders'] });
    const first = fresh[0];
    if (!first) return;
    const target = notificationTarget(first);
    toast(first.title, {
      id: 'new-public-orders',
      description: fresh.length > 1 ? `${fresh.length} nouvelles commandes reçues` : first.body,
      action: target
        ? {
            label: 'Voir la commande',
            onClick: () => {
              navigate(target);
              void api
                .post(`/notifications/${first.id}/read`)
                .then(() => client.invalidateQueries({ queryKey: ['notifications'] }))
                .catch(() => {});
            },
          }
        : undefined,
    });
  }, [query.data, scope, enabled, navigate, client]);
  return null;
}
